<template>
  <v-card>
    <v-card-title>
      {{ form.id ? 'バックシステム編集' : '新規バックシステム' }}
    </v-card-title>

    <v-card-text>
      <v-text-field
        v-model="form.name"
        label="名前"
        :error="!isValid.name"
      />

      <v-textarea
        v-model="form.description"
        label="説明"
        rows="2"
        :prepend-icon="icons.mdiCommentQuoteOutline"
      />

      <v-text-field
        v-model="form.bootTimeAt"
        label="システム基準日時"
        type="datetime-local"
        hide-details
        :error="!isValid.bootTimeAt"
        :min="dateTimeForJsFriendly(currentClub.createdAt)"
        class="pa-0 ma-0 my-8"
      />

      <div class="mt-4">
        <h4>
          構成ユニット
          ({{ form.rebateSystemUnitOrchestrationsAttributes.length }})
        </h4>

        <yona-has-many-v-select
          v-model="form.rebateSystemUnitOrchestrationsAttributes"
          parent-name="rebateSystem"
          :parent-id="form.id"
          :children="rebateSystemUnits"
          child-name="rebateSystemUnit"
          :error="!isValid.rebateSystemUnitOrchestrationsAttributes"
        />
      </div>

      <div class="mt-4">
        <h4>
          所属ユーザー
          ({{ form.rebateSystemUsersAttributes.length }})
        </h4>

        <yona-has-many-v-select
          v-model="form.rebateSystemUsersAttributes"
          parent-name="rebateSystem"
          :parent-id="form.id"
          :children="userItems"
          child-name="user"
          :error="!isValid.rebateSystemUsersAttributes"
        />
      </div>
    </v-card-text>

    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn
        color="primary"
        :disabled="!(Object.values(isValid).every(isValid => isValid))"
        :loading="isSubmitting"
        :ripple="false"
        @click="$emit('submit', form)"
      >
        {{ rebateSystem.id ? '更新' : '作成' }}
      </v-btn>

      <v-btn
        color="blue darken-1"
        text
        :ripple="false"
        @click="$emit('close')"
      >
        キャンセル
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import {
  ref,
  computed,
  onMounted,
} from '@vue/composition-api'
import {
  map,
  chain,
  uniqWith,
  isEqual,
  groupBy,
  each,
  keys,
} from 'lodash'
import {
  mdiCommentQuoteOutline,
} from '@mdi/js'
import useValidation from '@/views/composable/useValidation'
import useCurrentData from '@/views/composable/useCurrentData'
import useDateFormat from '@/views/composable/useDateFormat'
import YonaHasManyVSelect from '@/views/components/util/YonaHasManyVSelect.vue'

export default {
  components: {
    YonaHasManyVSelect,
  },
  props: {
    rebateSystem: {
      type: Object,
      required: true,
      default: () => {},
    },
    rebateSystemUnits: {
      type: Array,
      required: true,
      default: () => [],
    },
    users: {
      type: Array,
      required: true,
      default: () => [],
    },
    isSubmitting: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const { isValidDate } = useValidation()
    const { currentClub } = useCurrentData()
    const { dateTimeForJsFriendly } = useDateFormat()
    const form = ref({
      name: '',
      description: '',
      bootTimeAt: dateTimeForJsFriendly(new Date()),
      rebateSystemUsersAttributes: [],
      rebateSystemUnitOrchestrationsAttributes: [],
    })

    const userItems = computed(() => {
      const roleMsg = {
        cast: 'キャスト',
        waiter: 'スタッフ',
        alliance: 'アライアンス',
      }
      const lists = []
      const usersGrouped = groupBy(props.users, 'attributes.role')

      each(keys(roleMsg), role => {
        if (!usersGrouped[role]) return

        lists.push({ header: roleMsg[role] })
        lists.push(...usersGrouped[role])
      })

      return lists
    })

    const isEdit = computed(() => !!props.rebateSystem.id)

    const isValidRebateSystemUsersAttributes = computed(() => {
      const { rebateSystemUsersAttributes } = form.value

      const isUniq = chain(rebateSystemUsersAttributes)
        .map(o => [+o.userId, +o.rebateSystemId])
        .uniqWith(isEqual)
        .value()
        .length === rebateSystemUsersAttributes.length

      return isUniq
    })

    const isValidRebateSystemUnitOrchestrationsAttributes = computed(() => {
      const { rebateSystemUnitOrchestrationsAttributes } = form.value

      const isUniq = chain(rebateSystemUnitOrchestrationsAttributes)
        .map(o => [+o.rebateSystemId, +o.rebateSystemUnitId])
        .uniqWith(isEqual)
        .value()
        .length === rebateSystemUnitOrchestrationsAttributes.length

      return isUniq
    })

    const isValid = computed(() => {
      const { name, bootTimeAt } = form.value

      return {
        name,
        bootTimeAt: isValidDate(bootTimeAt.split(' ').join('T')) && new Date(currentClub.value.createdAt) <= new Date(bootTimeAt.split(' ').join('T')),
        rebateSystemUsersAttributes: isValidRebateSystemUsersAttributes.value,
        rebateSystemUnitOrchestrationsAttributes: isValidRebateSystemUnitOrchestrationsAttributes.value,
      }
    })

    const initForm = () => {
      if (isEdit.value) {
        const {
          name,
          description,
          bootTimeAt,
          rebateSystemUsers,
          rebateSystemUnitOrchestrations,
        } = props.rebateSystem.attributes

        const rebateSystemUsersAttributes = map(rebateSystemUsers.data, o => {
          const { userId, rebateSystemId } = o.attributes

          return {
            id: o.id,
            userId,
            rebateSystemId,
            _destroy: false,
          }
        })

        const rebateSystemUnitOrchestrationsAttributes = map(rebateSystemUnitOrchestrations.data, o => {
          const {
            lastCalcAt,
            position,
            rebateSystemId,
            rebateSystemUnitId,
          } = o.attributes

          return {
            id: o.id,
            lastCalcAt,
            position,
            rebateSystemId,
            rebateSystemUnitId,
            _destroy: false,
          }
        })

        form.value = {
          id: props.rebateSystem.id,
          name,
          description,
          bootTimeAt: dateTimeForJsFriendly(bootTimeAt),
          rebateSystemUsersAttributes,
          rebateSystemUnitOrchestrationsAttributes,
        }
      }
    }

    onMounted(() => initForm())

    return {
      // data
      form,

      // computed
      userItems,
      isEdit,
      isValid,

      // methods
      dateTimeForJsFriendly,

      icons: {
        mdiCommentQuoteOutline,
      },
    }
  },
}
</script>
